import axiosAPI from '../../utils/client/axiosAPI';
import {
  UserResponse,
  UserRegisterPayload,
  UserLoginPayload,
  UserRole
} from './contracts';

export class User {
  public readonly id: number;
  public readonly name: string;
  public readonly email: string;
  public readonly type: string;
  public readonly idNumber?: string;
  public readonly taxId?: string;

  protected readonly authorization: { roles: string[]; permissions: string[] };
  protected token: string;

  public constructor({
    id,
    name,
    email,
    type,
    id_number,
    tax_id,
    roles,
    permissions
  }: UserResponse) {
    this.id = id;
    this.name = name;
    this.email = email;
    this.type = type;
    this.idNumber = id_number;
    this.taxId = tax_id;
    this.authorization = {
      roles: roles ?? [],
      permissions: permissions ?? []
    };
    this.token = '';
  }

  public setToken(token: string): User {
    this.token = token;
    return this;
  }

  public getToken(): string {
    return this.token;
  }

  public hasRole(role: string): boolean {
    return this.hasAuthorization('roles', role);
  }

  public hasAnyRole(roles: string[]): boolean {
    return this.hasAnyAuthorization('roles', roles);
  }

  public hasRoles(roles: string[]): boolean {
    return this.hasAuthorizations('roles', roles);
  }

  public can(permission: string): boolean {
    return this.hasAuthorization('permissions', permission);
  }

  public canAny(permissions: string[]): boolean {
    return this.hasAuthorizations('permissions', permissions);
  }

  public canAll(permissions: string[]): boolean {
    return this.hasAuthorizations('permissions', permissions);
  }

  public static clone(user: User): User {
    return new User({
      id: user.id,
      name: user.name,
      email: user.email,
      type: user.type,
      id_number: user.idNumber,
      tax_id: user.taxId,
      roles: user.authorization.roles.slice(),
      permissions: user.authorization.permissions.slice()
    }).setToken(user.token);
  }

  public static async register(payload: UserRegisterPayload): Promise<User> {
    const {
      data: { user, token }
    } = await axiosAPI.post('/auth/register', payload);
    return new User(user).setToken(token);
  }

  public static async login(payload: UserLoginPayload): Promise<User> {
    const {
      data: { user, token }
    } = await axiosAPI.post('/auth/login', payload);
    return new User(user).setToken(token);
  }

  public static async fetchByToken(token: string): Promise<User> {
    const { data } = await axiosAPI.get<UserResponse>('/user', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return new User(data).setToken(token);
  }

  public static async getAllRoles(): Promise<UserRole[]> {
    const { data } = await axiosAPI.get<UserRole[]>('/user/roles');
    return data;
  }

  protected hasAuthorization(
    key: 'roles' | 'permissions',
    authorization: string
  ): boolean {
    return this.authorization[key].includes(authorization);
  }

  protected hasAnyAuthorization(
    key: 'roles' | 'permissions',
    authorizations: string[]
  ): boolean {
    return this.authorization[key].some(
      Set.prototype.has,
      new Set(authorizations)
    );
  }

  protected hasAuthorizations(
    key: 'roles' | 'permissions',
    authorizations: string[]
  ): boolean {
    return this.authorization[key].every(
      Set.prototype.has,
      new Set(authorizations)
    );
  }
}
